import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { IBaseResponse } from '@carm/_utils/models';
import { map, mergeMap, share, tap } from 'rxjs/operators';
import { ICadDraftUiContent, IStep } from '../models/declarations.model';
import { StepperService } from './stepper.service';
import { HttpParams } from '@angular/common/http';
import { RoutesConst } from '@carm/shared/constants/const-routes';
import { LoaderSpinnerTypes } from '@carm/_core';
import * as dayjs from 'dayjs';
import { dateFormat } from '@carm/shared/constants/const-carm';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { ICadInvoiceListResponse, ICadGoodsListResponse, ICadDraftHeaderResponse, IGetDraftsResponse,
  ICadDraftHeaderRequest, ICreateCadDraftHeaderResponse, IDraftResponse, IDeleteDraftRequest,
  IBaseCadDraftResponse, ICargoListResponse, ICadInvoiceResponse, ICadDraftInvoiceRequest,
  ICadGoodResponse, ICadGoodRequest, IGetDeclarationResponse, ICadDraftReason }
from '../models/api';
import { IStepState } from '../models/common';

@Injectable()
export class CadDraftService extends BaseApiService {
  private readonly invoiceCountChangeSubject = new BehaviorSubject<boolean>(
    false
  );
  private readonly goodCountChangeSubject = new BehaviorSubject<boolean>(false);
  private readonly invoiceListChangeSubject = new BehaviorSubject<ICadInvoiceListResponse>(null);
  private readonly goodsListChangeSubject = new BehaviorSubject<Map<string, ICadGoodsListResponse>>(new Map<string, ICadGoodsListResponse>());
  private readonly draftHeaderChangeSubject = new BehaviorSubject<ICadDraftHeaderResponse>(null);

  get declarationDraft(): string {
    return `${this.endPointBP}/cad/declaration/draft`;
  }

  constructor(
    protected injector: Injector,
    private stepperService: StepperService
  ) {
    super(injector);
  }

  public get onInvoiceCountChange$(): Observable<boolean> {
    return this.invoiceCountChangeSubject.asObservable();
  }

  public get onGoodCountChange$(): Observable<boolean> {
    return this.goodCountChangeSubject.asObservable();
  }

  public get onGetDraftHeaderChange$(): Observable<ICadDraftHeaderResponse> {
    return this.draftHeaderChangeSubject.asObservable();
  }

  public get draftHeaderSnapshot(): ICadDraftHeaderResponse {
    return this.draftHeaderChangeSubject.getValue();
  }

  public get onInvoiceListChanged$(): Observable<ICadInvoiceListResponse> {
    return this.invoiceListChangeSubject.asObservable();
  }

  public get onInvoiceListSnapshot(): ICadInvoiceListResponse {
    return this.invoiceListChangeSubject.getValue();
  }

  public get goodsListChanged$(): Observable<
    Map<string, ICadGoodsListResponse>
  > {
    return this.goodsListChangeSubject.asObservable();
  }

  public get goodsListSnapshot(): Map<string, ICadGoodsListResponse> {
    return this.goodsListChangeSubject.getValue();
  }

  getDeclarationDrafts(
    minDate: Date,
    maxDate: Date,
    currentPage: number,
    pageSize: number,
    sortBy: string,
    filterField: string,
    filterValue: string
  ): Observable<IGetDraftsResponse> {
    const endPoint = `${this.declarationDraft}`;
    const params = new HttpParams()
      .set(
        'startDate',
        minDate
          ? dayjs(minDate).format(dateFormat)
          : dayjs().subtract(100, 'years').format(dateFormat)
      )
      .set(
        'endDate',
        maxDate
          ? dayjs(maxDate).format(dateFormat)
          : dayjs().format(dateFormat)
      ) // add day to include last day
      .set('pageSize', pageSize.toString())
      .set('currentPage', currentPage.toString())
      .set('sort', sortBy)
      .set('filterField', filterField)
      .set('filterValue', filterValue);
    return this.httpAuthGet<IGetDraftsResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  createDraftNew(
    draft: ICadDraftHeaderRequest
  ): Observable<ICreateCadDraftHeaderResponse> {
    this.draftHeaderChangeSubject.next(null);

    const draftUiContent: ICadDraftUiContent = {
      stepStates: this.stepperService
        .getDeclarationStepsByType(draft.declarationTypeCode)
        .map(step => ({ id: step.id, isComplete: false } as IStepState)),
    };

    const draftUiContentJson = JSON.stringify(draftUiContent);
    draft.draftUiContent = draftUiContentJson;
    this.goodsListChangeSubject.next(new Map<string, ICadGoodsListResponse>()); // clear goods list before create
    return this.httpAuthPost<ICreateCadDraftHeaderResponse>(
      this.declarationDraft,
      draft
    );
  }

  getDraft(uid: string): Observable<ICadDraftHeaderResponse> {
    const endPoint = `${this.declarationDraft}/${uid}`;
    return this.httpAuthGet<ICadDraftHeaderResponse>(endPoint, null).pipe(
      tap(draft => {
        if (draft) {
          this.goodsListChangeSubject.next(new Map<string, ICadGoodsListResponse>()); // clear existing goods list
          this.draftHeaderChangeSubject.next(draft);
        }
      }),
      share()
    );
  }

  updateDraft(
    draftUid: string,
    draft: ICadDraftHeaderRequest,
    stepState: IStepState
  ): Observable<ICadDraftHeaderResponse> {
    if (stepState) {
      let draftUiContent: ICadDraftUiContent;

      if (draft.draftUiContent) {
        draftUiContent = JSON.parse(draft.draftUiContent);

        // if json gets corrupted, or unknown state
        if(!draftUiContent?.stepStates?.some(s => s.id === stepState.id)){
          draftUiContent = {
            stepStates: this.stepperService
            .getDeclarationStepsByType(draft.declarationTypeCode)
            .map(step => ({ id: step.id, isComplete: false } as IStepState))
          };
        }
      } else {
        // if draftUiContent was removed, create a new one
        draftUiContent = {
          stepStates: this.stepperService
            .getDeclarationStepsByType(draft.declarationTypeCode)
            .map(step => ({ id: step.id, isComplete: false } as IStepState)),
        };
      }

      const currentState = draftUiContent.stepStates.find(
        ss => ss.id === stepState.id
      );

      if (currentState) {
        currentState.isComplete = stepState.isComplete;
      } else {
        draftUiContent.stepStates.push(stepState); // a new step not part of draft?
      }

      draft.draftUiContent = JSON.stringify(draftUiContent);
    }

    const endPoint = `${this.declarationDraft}/${draftUid}`;
    return this.httpAuthPut<ICadDraftHeaderResponse>(endPoint, draft).pipe(
      tap(draftResponse => {
        if (draftResponse) {
          this.draftHeaderChangeSubject.next(draftResponse);
        }
      })
    );
  }

  updateDraftOld(draftData: any): Observable<ICadDraftHeaderResponse> {
    // request type?
    const endPoint = `${this.declarationDraft}/${draftData.draftUid}`;
    return this.httpAuthPut<ICadDraftHeaderResponse>(endPoint, draftData);
  }

  deleteDraft(draftUid: string, etag: string): Observable<IDraftResponse> {
    const deleteRequest: IDeleteDraftRequest = {
      etag,
    };

    const endPoint = `${this.declarationDraft}/${draftUid}`;
    return this.httpAuthDelete<IDraftResponse>(endPoint, null, deleteRequest);
  }

  createCargo(
    draftUid: string,
    cargoControlValue: string
  ): Observable<IBaseCadDraftResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/cargo`;
    return this.httpAuthPost<IBaseCadDraftResponse>(endPoint, {
      cargoControlNo: cargoControlValue,
    });
  }

  getCargoList(draftUid: string): Observable<ICargoListResponse> {
    const maxPageSize = 250;
    const endPoint = `${
      this.declarationDraft
    }/${draftUid}/cargo?pageSize=${maxPageSize}`;
    return this.httpAuthGet<ICargoListResponse>(endPoint, null).pipe(map(this.filterEmptyCargo));
  }

  deleteCargo(draftUid: string, cargoUid: string, isNew: boolean, etag: string) {
    const endPoint = `${
      this.declarationDraft
    }/${draftUid}/cargo/${cargoUid}`;
    return this.httpAuthDelete<any>(endPoint, null, { etag, isNew: isNew ?? false });
  }

  getInvoice(
    draftUid: string,
    invoiceUid: string
  ): Observable<ICadInvoiceResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceUid}`;
    return this.httpAuthGet<ICadInvoiceResponse>(endPoint);
  }

  createInvoice(
    draftUid: string,
    invoiceRequest: ICadDraftInvoiceRequest
  ): Observable<IBaseCadDraftResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice`;
    return this.httpAuthPost<IBaseCadDraftResponse>(
      endPoint,
      invoiceRequest
    ).pipe(
      tap(_ => {
        this.invoiceCountChangeSubject.next(true);
      })
    );
  }

  updateInvoice(
    draftUid: string,
    invoiceRequest: ICadDraftInvoiceRequest
  ): Observable<ICadInvoiceResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceRequest.uid}`;
    return this.httpAuthPut<ICadInvoiceResponse>(endPoint, invoiceRequest);
  }

  deleteInvoiceById(
    draftUid: string,
    invoiceUid: string
  ): Observable<IBaseResponse> {
    return this.getInvoice(draftUid, invoiceUid).pipe(
      mergeMap(invoiceResponse => {
        const deleteInvoiceRequest: ICadDraftInvoiceRequest = {
          invoice: invoiceResponse,
        };

        const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceUid}`;
        return this.httpAuthDelete<IBaseResponse>(
          endPoint,
          null,
          deleteInvoiceRequest
        ).pipe(
          tap(_ => {
            this.invoiceCountChangeSubject.next(true);
          })
        );
      })
    );
  }

  deleteInvoice(
    draftUid: string,
    invoiceRequest: ICadDraftInvoiceRequest
  ): Observable<IBaseResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceRequest.uid}`;
    return this.httpAuthDelete<IBaseResponse>(
      endPoint,
      null,
      invoiceRequest
    ).pipe(
      tap(_ => {
        this.invoiceCountChangeSubject.next(true);
      })
    );
  }

  getInvoiceList(
    draftUid: string,
    currentPage: number,
    pageSize: number,
    sortBy: string = 'INVOICE_LINE_NUMBER_ASC'
  ): Observable<ICadInvoiceListResponse> {
    const params = new HttpParams()
      .set('pageSize', pageSize ? pageSize.toString() : '10')
      .set('currentPage', currentPage ? currentPage.toString() : '1')
      .set('sort', sortBy);

    const endPoint = `${
      this.declarationDraft
    }/${draftUid}/invoice`;
    return this.httpAuthGet<ICadInvoiceListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid).pipe(
      tap(invoicesResponse => {
        if (invoicesResponse) {
          this.invoiceListChangeSubject.next(invoicesResponse);
        }
      })
    );
  }

  getGoodsList(
    draftUid: string,
    invoiceUid: string,
    loaderSpinnerType: LoaderSpinnerTypes,
    currentPage: number = 1,
    pageSize: number = 500,
    sortBy: string = 'GOOD_LINE_NO_ASC',
  ): Observable<ICadGoodsListResponse> {
    const params = new HttpParams()
      .set('pageSize', pageSize.toString())
      .set('sort', sortBy)
      .set('currentPage', currentPage.toString());

    const endPoint = `${
      this.declarationDraft
    }/${draftUid}/invoice/${invoiceUid}/good`;
    return this.httpAuthGet<ICadGoodsListResponse>(endPoint, params, loaderSpinnerType).pipe(
      tap(goodsListResponse => {
        if (goodsListResponse) {
          const snapshot = this.goodsListSnapshot;
          snapshot.set(invoiceUid, goodsListResponse);
          this.goodsListChangeSubject.next(snapshot);
        }
      })
    );
  }

  getGood(
    draftUid: string,
    invoiceUid: string,
    goodUid: string
  ): Observable<ICadGoodResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceUid}/good/${goodUid}`;
    return this.httpAuthGet<ICadGoodResponse>(endPoint);
  }

  createGood(
    draftUid: string,
    invoiceUid: string,
    createGoodRequest: ICadGoodRequest
  ): Observable<IBaseCadDraftResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceUid}/good`;
    return this.httpAuthPost<IBaseCadDraftResponse>(
      endPoint,
      createGoodRequest
    ).pipe(
      tap(_ => {
        this.goodCountChangeSubject.next(true);
      })
    );
  }

  updateGood(
    draftUid: string,
    invoiceUid: string,
    updateGoodRequest: ICadGoodRequest
  ): Observable<ICadGoodResponse> {
    const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceUid}/good/${updateGoodRequest.good.goodUid}`;
    return this.httpAuthPut<ICadGoodResponse>(endPoint, updateGoodRequest);
  }

  deleteGoodById(
    draftUid: string,
    invoiceUid: string,
    goodUid: string,
    reasons?: ICadDraftReason[]
  ): Observable<ICadGoodResponse> {
    return this.getGood(draftUid, invoiceUid, goodUid).pipe(
      mergeMap(goodResponse => {
        goodResponse.reasons = reasons;
        const deleteGoodRequest: ICadGoodRequest = {
          good: goodResponse,
        };

        const endPoint = `${this.declarationDraft}/${draftUid}/invoice/${invoiceUid}/good/${goodUid}`;
        return this.httpAuthDelete<ICadGoodResponse>(
          endPoint,
          null,
          deleteGoodRequest
        ).pipe(
          tap(_ => {
            this.goodCountChangeSubject.next(true);
          })
        );
      })
    );
  }

  validateDraftDeclaration(
    draftUid: string,
    forceValidation: boolean
  ): Observable<IBaseResponse> {
    const allGoods = Array.from(this.goodsListSnapshot.values()); // get all goods from all invoices

    if (forceValidation || allGoods.some(x => x.pagination.totalCount > 0)) {
      // validate if some goods already added
      const endPoint = `${this.declarationDraft}/validate/${draftUid}`;
      return this.httpAuthPost<IBaseResponse>(endPoint, null);
    } else {
      return of(null); // don't validate if no goods present.
    }
  }

  submitDraftDeclaration(draftUid: string): Observable<IGetDeclarationResponse> {
    const endPoint = `${this.declarationDraft}/submit/${draftUid}`;
    return this.httpAuthPost<IGetDeclarationResponse>(endPoint, null).pipe(
      tap(_ => {
        this.draftHeaderChangeSubject.next(null); // clean slate for next
      })
    );

  }

  filterEmptyCargo(cargoResponse:ICargoListResponse): ICargoListResponse{
     var cargoList =  cargoResponse.results.filter(c=>c.cargoControlNo !== 'N/A');
     cargoResponse.results = cargoList;
     return cargoResponse;
  }

  getLastEditRoute(draftUiContentRaw: string, allSteps: IStep[]): string {
    if (!draftUiContentRaw) {
      return RoutesConst.Declarations_Mgt_OverviewRel;
    }

    const completedStates = (
      JSON.parse(draftUiContentRaw) as ICadDraftUiContent
    )?.stepStates?.filter(s => s.isComplete);
    const allStepsSorted = [...allSteps].sort((a, b) => b.order - a.order);

    if (completedStates?.length === 0) {
      return RoutesConst.Declarations_Mgt_OverviewRel;
    }

    for (let i = 0; i < allStepsSorted.length; i++) {
      if (
        completedStates.find(s => allStepsSorted[i].id === s.id)?.isComplete
      ) {
        return allStepsSorted[i - 1].routeName;
      }
    }
  }
}
